import React from 'react';
import { Tooltip } from 'antd';
import { ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { LocationData, DataStatus, DateRange } from '../../services/DashboardService';
import {
  formatCurrency,
  formatPercent,
  formatPercentWithSign,
  formatCurrencyChange,
  getDisplayLocationName,
  formatDisplayDate,
} from '../../utils/formatters';
import { normalizeDate } from '../../utils/dateUtils';
import { AnomalyFlag } from '../../utils/anomalyDetection';
import { BottomPerformer } from '../../utils/bottomPerformers';

interface GetColumnsProps {
  sortBy: string;
  viewMode: 'weekly' | 'quarterly';
  dataStatus: DataStatus | null;
  anomalyFlags?: Map<string, AnomalyFlag>;
  bottomPerformers?: Map<string, BottomPerformer>;
  selectedDateRange?: DateRange | null;
}

export const getDashboardColumns = ({ sortBy, viewMode, dataStatus, anomalyFlags, bottomPerformers, selectedDateRange }: GetColumnsProps) => {
  const isQuarterly = viewMode === 'quarterly';
  
  // Calculate prior week date range for display (last week of the 8-week range)
  const priorWeekDateRange = selectedDateRange && !isQuarterly
    ? (() => {
        // The "prior week" is the last week (8th week) of the 8-week range
        // The backend uses ISO week boundaries (Monday-Sunday)
        // We need to find the ISO week containing endDate
        const endDate = normalizeDate(selectedDateRange.endDate);
        const startDate = normalizeDate(selectedDateRange.startDate);
        
        // Get ISO week boundaries for the week containing endDate
        // ISO weeks start on Monday (day 1) and end on Sunday (day 0)
        const dayOfWeek = endDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        
        // Calculate days to subtract to get to Monday of the ISO week
        // If Sunday (0), go back 6 days to get Monday; otherwise go back (dayOfWeek - 1) days
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        
        const isoWeekMonday = new Date(endDate);
        isoWeekMonday.setDate(isoWeekMonday.getDate() - daysToMonday);
        
        // The week start is the ISO week Monday, but don't go before the range start
        const weekStart = isoWeekMonday >= startDate ? isoWeekMonday : startDate;
        
        // The week end is the endDate (Sunday of the ISO week, capped at range end)
        const weekEnd = endDate;
        
        const formatDate = (date: Date): string => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        
        return {
          start: formatDate(weekStart),
          end: formatDate(weekEnd),
          displayText: `${formatDisplayDate(formatDate(weekStart))}-${formatDisplayDate(formatDate(weekEnd))}`,
          withParentheses: `(${formatDisplayDate(formatDate(weekStart))}-${formatDisplayDate(formatDate(weekEnd))})`,
        };
      })()
    : null;
  const priorWeekDateRangeText = priorWeekDateRange?.withParentheses || '';
  const getEightWeekPercent = (record: LocationData): number | null => {
    if (record.eightWeekGfsPercentValue !== undefined && record.eightWeekGfsPercentValue !== null) {
      return record.eightWeekGfsPercentValue;
    }
    if (
      !record.eightWeekTotalSales ||
      record.eightWeekTotalSales <= 0 ||
      record.eightWeekTotalGfs === null ||
      record.eightWeekTotalGfs === undefined
    ) {
      return null;
    }
    return (record.eightWeekTotalGfs / record.eightWeekTotalSales) * 100;
  };

  // Create render function that captures current flags and performers
  const renderLocationWithIndicators = (text: string, record: LocationData) => {
    // Always use location name, not DM name when in DM view
    const locationName = getDisplayLocationName(record.location);
    
    // Get current values from the maps (these should be up-to-date)
    const anomaly = anomalyFlags?.get(record.location);
    const bottomPerformer = bottomPerformers?.get(record.location);
    
    const indicators: React.ReactNode[] = [];
    
    // Debug: Log when we have maps but no flags for this location (only first few to avoid spam)
    if (anomalyFlags && anomalyFlags.size > 0 && indicators.length === 0) {
      const isInMap = anomalyFlags.has(record.location);
      if (isInMap) {
        console.warn(`Found anomaly for location ${record.location}`);
      }
    }
    if (bottomPerformers && bottomPerformers.size > 0 && indicators.length === 0) {
      const isInMap = bottomPerformers.has(record.location);
      if (isInMap) {
        console.warn(`Found bottom performer for location ${record.location}`);
      }
    }
    
    if (anomaly) {
      const Icon = anomaly.severity === 'critical' ? ExclamationCircleOutlined : WarningOutlined;
      const color = anomaly.severity === 'critical' ? '#ff4d4f' : '#faad14';
      const compLoc = anomaly.details.comparisonLocation;
      
      indicators.push(
        <Tooltip
          key="anomaly"
          title={
            <div style={{ minWidth: 300 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: '13px' }}>
                {anomaly.severity === 'critical' ? '⚠️ Critical' : '⚠️ Warning'}: Higher Prior Week GFS%
              </div>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #d9d9d9' }}>
                    <th style={{ textAlign: 'left', padding: '4px', fontWeight: 600 }}>Location</th>
                    <th style={{ textAlign: 'right', padding: '4px', fontWeight: 600 }}>
                      {priorWeekDateRange ? `${priorWeekDateRange.displayText} Sales` : 'Prior Week Sales'}
                    </th>
                    <th style={{ textAlign: 'right', padding: '4px', fontWeight: 600 }}>
                      {priorWeekDateRange ? `${priorWeekDateRange.displayText} GFS` : 'Prior Week GFS'}
                    </th>
                    <th style={{ textAlign: 'right', padding: '4px', fontWeight: 600 }}>
                      {priorWeekDateRange ? `${priorWeekDateRange.displayText} GFS%` : 'Prior Week GFS%'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: 'rgba(255, 77, 79, 0.1)' }}>
                    <td style={{ padding: '4px', fontWeight: 600 }}>{record.location}</td>
                    <td style={{ textAlign: 'right', padding: '4px' }}>{formatCurrency(anomaly.details.sales)}</td>
                    <td style={{ textAlign: 'right', padding: '4px' }}>{formatCurrency(anomaly.details.gfsSpending)}</td>
                    <td style={{ textAlign: 'right', padding: '4px', fontWeight: 600, color: '#ff4d4f' }}>
                      {formatPercent(anomaly.details.gfsPercent)}
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: 'rgba(82, 196, 26, 0.1)' }}>
                    <td style={{ padding: '4px' }}>{compLoc.name}</td>
                    <td style={{ textAlign: 'right', padding: '4px' }}>{formatCurrency(compLoc.sales)}</td>
                    <td style={{ textAlign: 'right', padding: '4px' }}>{formatCurrency(compLoc.gfsSpending)}</td>
                    <td style={{ textAlign: 'right', padding: '4px', color: '#52c41a' }}>
                      {formatPercent(compLoc.gfsPercent)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: 8, fontSize: '11px', color: '#8c8c8c' }}>
                <div>Prior week sales difference: {formatCurrency(anomaly.details.salesDifference)}</div>
                <div>Prior week GFS% difference: {anomaly.details.gfsPercentDifference.toFixed(1)} points</div>
              </div>
            </div>
          }
          overlayStyle={{ maxWidth: 400 }}
        >
          <Icon style={{ color, marginLeft: 4, fontSize: 14 }} />
        </Tooltip>
      );
    }
    
    if (bottomPerformer && !isQuarterly) {
      const eightWeekGfs = bottomPerformer.location.eightWeekTotalGfs ?? 0;
      const eightWeekSales = bottomPerformer.location.eightWeekTotalSales ?? 0;
      const eightWeekGfsPercent = bottomPerformer.gfsPercent;
      
      indicators.push(
        <Tooltip
          key="bottom-performer"
          title={
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Bottom Performer #{bottomPerformer.rank}</div>
              <div style={{ fontSize: '12px' }}>
                <div>Prior Week GFS%: {formatPercent(eightWeekGfsPercent)}</div>
                <div>Prior Week Total Sales: {formatCurrency(eightWeekSales)}</div>
                <div>Prior Week GFS: {formatCurrency(eightWeekGfs)}</div>
              </div>
            </div>
          }
        >
          <span style={{ color: '#ff4d4f', marginLeft: 4, fontSize: 12, fontWeight: 'bold' }}>
            #{bottomPerformer.rank}
          </span>
        </Tooltip>
      );
    }
    
    return (
      <span>
        <b>{locationName}</b>
        {indicators}
      </span>
    );
  };

  const locationColumns = [
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 200,
      fixed: 'left' as const,
      render: renderLocationWithIndicators,
    },
    {
      title: 'DM',
      dataIndex: 'group',
      key: 'group',
      width: 110,
      render: (text: string) => text || '--',
    },
  ];

  const renderTrendValue = (
    value: number | null | undefined,
    formatter: (val: number) => string,
    invertColor = false
  ) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '--';
    }
    const positive = value > 0;
    const negative = value < 0;
    const color = positive
      ? invertColor ? '#ff4d4f' : '#52c41a'
      : negative
        ? invertColor ? '#52c41a' : '#ff4d4f'
        : undefined;
    return <span style={{ color }}>{formatter(value)}</span>;
  };

  if (isQuarterly) {
    const prefix = 'Qtr Weekly Avg';
    const quarterlyColumns = [
      {
        title: `${prefix} Toast`,
        dataIndex: 'weeklySalesToast',
        key: 'quarterWeeklyToast',
        align: 'right' as const,
        render: (value: number | null) => (value === null ? '--' : formatCurrency(value)),
      },
      {
        title: `${prefix} Cater`,
        dataIndex: 'weeklyCatering',
        key: 'quarterWeeklyCater',
        align: 'right' as const,
        render: (value: number | null) => (value === null ? '--' : formatCurrency(value)),
      },
      {
        title: `${prefix} Total Sales`,
        dataIndex: 'weeklyTotal',
        key: 'quarterWeeklySales', // Maps to SORTABLE_COLUMNS.quarterWeeklySales
        align: 'right' as const,
        sorter: (a: LocationData, b: LocationData) => {
          const aVal = a.weeklyTotal ?? 0;
          const bVal = b.weeklyTotal ?? 0;
          return aVal - bVal;
        },
        render: (value: number | null) => (value === null ? '--' : formatCurrency(value)),
      },
      {
        title: `${prefix} Gfs Spend`,
        dataIndex: 'weeklyGfs',
        key: 'quarterWeeklyGfs', // Maps to SORTABLE_COLUMNS.quarterWeeklyGfs
        align: 'right' as const,
        sorter: (a: LocationData, b: LocationData) => {
          const aVal = a.weeklyGfs ?? 0;
          const bVal = b.weeklyGfs ?? 0;
          return aVal - bVal;
        },
        render: (value: number | null) => (value === null || value === 0 ? '--' : formatCurrency(value)),
      },
      {
        title: `${prefix} Gfs %`,
        dataIndex: 'gfsPercent',
        key: 'gfsPercent', // Maps to SORTABLE_COLUMNS.gfsPercent
        align: 'right' as const,
        sorter: (a: LocationData, b: LocationData) => {
          const aVal = a.gfsPercent ?? 0;
          const bVal = b.gfsPercent ?? 0;
          return aVal - bVal;
        },
        render: (value: number | null) => (value === null || value === 0 ? '--' : formatPercent(value)),
      },
      {
        title: `${prefix} Var from Avg`,
        dataIndex: 'varianceFromAvg',
        key: 'varianceFromAvg', // Maps to SORTABLE_COLUMNS.varianceFromAvg
        align: 'right' as const,
        sorter: (a: LocationData, b: LocationData) => {
          const aVal = a.varianceFromAvg ?? 0;
          const bVal = b.varianceFromAvg ?? 0;
          return aVal - bVal;
        },
        render: (value: number) => {
          const color = value <= -2 ? '#52c41a' : value >= 2 ? '#ff4d4f' : undefined;
          return <span style={{ color }}>{formatPercentWithSign(value)}</span>;
        },
      },
      {
        title: 'GFS % PRVS QTR CHG',
        key: 'gfsPercentTrend',
        align: 'right' as const,
        render: (_: any, record: LocationData) =>
          renderTrendValue(record.gfsPercentTrend, formatPercentWithSign, true),
      },
      {
        title: 'Toast Sales',
        dataIndex: 'quarterlyToastSales',
        key: 'quarterToastSales',
        align: 'right' as const,
        sorter: (a: LocationData, b: LocationData) => {
          const aVal = a.quarterlyToastSales ?? 0;
          const bVal = b.quarterlyToastSales ?? 0;
          return aVal - bVal;
        },
        render: (value: number | null | undefined) => (value !== null && value !== undefined && value > 0 ? formatCurrency(value) : '--'),
      },
      {
        title: 'Ez Cater Sales',
        dataIndex: 'quarterlyEzCaterSales',
        key: 'quarterEzCaterSales',
        align: 'right' as const,
        sorter: (a: LocationData, b: LocationData) => {
          const aVal = a.quarterlyEzCaterSales ?? 0;
          const bVal = b.quarterlyEzCaterSales ?? 0;
          return aVal - bVal;
        },
        render: (value: number | null | undefined) => (value !== null && value !== undefined && value > 0 ? formatCurrency(value) : '--'),
      },
      {
        title: 'Total Sales',
        dataIndex: 'eightWeekTotalSales',
        key: 'quarterTotalSales', // Maps to SORTABLE_COLUMNS.quarterTotalSales
        align: 'right' as const,
        sorter: (a: LocationData, b: LocationData) => {
          const aVal = a.eightWeekTotalSales ?? 0;
          const bVal = b.eightWeekTotalSales ?? 0;
          return aVal - bVal;
        },
        render: (value: number) => (value > 0 ? formatCurrency(value) : '--'),
      },
      {
        title: 'Total Sales PRVS QTR CHG',
        key: 'totalSalesTrend',
        align: 'right' as const,
        render: (_: any, record: LocationData) =>
          renderTrendValue(record.totalSalesTrend, formatCurrencyChange),
      },
      {
        title: 'Total GFS',
        dataIndex: 'eightWeekTotalGfs',
        key: 'quarterTotalGfs', // Maps to SORTABLE_COLUMNS.quarterTotalGfs
        align: 'right' as const,
        sorter: (a: LocationData, b: LocationData) => {
          const aVal = a.eightWeekTotalGfs ?? 0;
          const bVal = b.eightWeekTotalGfs ?? 0;
          return aVal - bVal;
        },
        render: (value: number | null) => (value === null || value === 0 ? '--' : formatCurrency(value)),
      },
      {
        title: 'Total GFS PRVS QTR CHG',
        key: 'totalGfsTrend',
        align: 'right' as const,
        render: (_: any, record: LocationData) =>
          renderTrendValue(record.totalGfsTrend, formatCurrencyChange),
      },
    ];

    return [...locationColumns, ...quarterlyColumns];
  }

  const priorWeekColumns = [
    {
      title: (
        <div>
          <div>Prior Week Total Sales</div>
          {priorWeekDateRangeText && <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#666' }}>{priorWeekDateRangeText}</div>}
        </div>
      ),
      dataIndex: 'eightWeekTotalSales',
      key: 'priorWeekTotalSales', // Maps to SORTABLE_COLUMNS.priorWeekTotalSales
      align: 'right' as const,
      sorter: (a: LocationData, b: LocationData) => {
        const aVal = a.eightWeekTotalSales ?? 0;
        const bVal = b.eightWeekTotalSales ?? 0;
        return aVal - bVal;
      },
      render: (value: number) =>
        value > 0 ? formatCurrency(value) : <span style={{ color: '#ff4d4f' }}>--</span>,
    },
    {
      title: (
        <div>
          <div>Prior Week GFS Spend</div>
          {priorWeekDateRangeText && <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#666' }}>{priorWeekDateRangeText}</div>}
        </div>
      ),
      dataIndex: 'eightWeekTotalGfs',
      key: 'priorWeekGfs', // Maps to SORTABLE_COLUMNS.priorWeekGfs
      align: 'right' as const,
      sorter: (a: LocationData, b: LocationData) => {
        const aVal = a.eightWeekTotalGfs ?? 0;
        const bVal = b.eightWeekTotalGfs ?? 0;
        return aVal - bVal;
      },
      render: (value: number | null) =>
        value !== null && value > 0 ? formatCurrency(value) : <span style={{ color: '#ff4d4f' }}>--</span>,
    },
    {
      title: 'Prior Week GFS %',
      key: 'priorWeekGfsPercent',
      align: 'right' as const,
      render: (_: any, record: LocationData) => {
        const percent = getEightWeekPercent(record);
        return percent !== null ? formatPercent(percent) : '--';
      },
    },
    {
      title: 'Prior Week GFS Var from Avg',
      dataIndex: 'eightWeekGfsVariance',
      key: 'priorWeekVariance',
      align: 'right' as const,
      render: (value: number | null | undefined) => {
        if (value === null || value === undefined) {
          return '--';
        }
        const color = value <= -2 ? '#52c41a' : value >= 2 ? '#ff4d4f' : undefined;
        return <span style={{ color }}>{formatPercentWithSign(value)}</span>;
      },
    },
  ];

  const avgColumns = [
    {
      title: '8-Wk Avg Toast',
      dataIndex: 'weeklySalesToast',
      key: 'avgToast',
      align: 'right' as const,
      render: (value: number | null) => {
        const isMissing = dataStatus && !dataStatus.toastDataAvailable;
        if (isMissing || value === null) {
          return <span style={{ color: '#ff4d4f' }}>--</span>;
        }
        return formatCurrency(value);
      },
    },
    {
      title: '8-Wk Avg Cater',
      dataIndex: 'weeklyCatering',
      key: 'avgCater',
      align: 'right' as const,
      render: (value: number | null) => {
        const isMissing = dataStatus && !dataStatus.ezcaterDataAvailable;
        if (isMissing || value === null || value === undefined) {
          return <span style={{ color: '#ff4d4f' }}>--</span>;
        }
        return formatCurrency(value);
      },
    },
    {
      title: '8-Wk Avg Weekly Sales',
      dataIndex: 'weeklyTotal',
      key: 'avgWeeklySales', // Maps to SORTABLE_COLUMNS.avgWeeklySales
      align: 'right' as const,
      sorter: (a: LocationData, b: LocationData) => {
        const aVal = a.weeklyTotal ?? 0;
        const bVal = b.weeklyTotal ?? 0;
        return aVal - bVal;
      },
      render: (value: number | null) => {
        const isMissing =
          dataStatus && (!dataStatus.toastDataAvailable || !dataStatus.ezcaterDataAvailable);
        if (isMissing || value === null) {
          return <span style={{ color: '#ff4d4f' }}>--</span>;
        }
        return formatCurrency(value);
      },
    },
    {
      title: '8-Wk Avg GFS Spend',
      dataIndex: 'weeklyGfs',
      key: 'avgWeeklyGfs', // Maps to SORTABLE_COLUMNS.avgWeeklyGfs
      align: 'right' as const,
      sorter: (a: LocationData, b: LocationData) => {
        const aVal = a.weeklyGfs ?? 0;
        const bVal = b.weeklyGfs ?? 0;
        return aVal - bVal;
      },
      render: (value: number | null) => {
        const isMissing = dataStatus && !dataStatus.gfsDataAvailable;
        if (isMissing || value === null || value === 0) {
          return <span style={{ color: '#ff4d4f' }}>--</span>;
        }
        return formatCurrency(value);
      },
    },
    {
      title: '8-Wk GFS %',
      dataIndex: 'gfsPercent',
      key: 'gfsPercent', // Maps to SORTABLE_COLUMNS.gfsPercent
      align: 'right' as const,
      sorter: (a: LocationData, b: LocationData) => {
        const aVal = a.gfsPercent ?? 0;
        const bVal = b.gfsPercent ?? 0;
        return aVal - bVal;
      },
      render: (value: number | null) => (value === null || value === 0 ? '--' : formatPercent(value)),
    },
    {
      title: '8-Wk GFS Var from Avg',
      dataIndex: 'varianceFromAvg',
      key: 'varianceFromAvg', // Maps to SORTABLE_COLUMNS.varianceFromAvg
      align: 'right' as const,
      sorter: (a: LocationData, b: LocationData) => {
        const aVal = a.varianceFromAvg ?? 0;
        const bVal = b.varianceFromAvg ?? 0;
        return aVal - bVal;
      },
      render: (value: number) => {
        const color = value <= -2 ? '#52c41a' : value >= 2 ? '#ff4d4f' : undefined;
        return <span style={{ color }}>{formatPercentWithSign(value)}</span>;
      },
    },
  ];

  return [...locationColumns, ...priorWeekColumns, ...avgColumns];
};

